import React, { createContext, useMemo, useContext, useState, useCallback, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useOrganization } from './OrganizationContext';

interface Tag {
  id: string;
  name: string;
  color: string;
}

interface TagsContextType {
  tags: Tag[];
  loading: boolean;
  refreshTags: () => Promise<void>;
  addTag: (name: string, color: string, createdBy: string) => Promise<Tag | null>;
  updateTag: (id: string, name: string, color: string) => Promise<boolean>;
  deleteTag: (id: string) => Promise<boolean>;
}

const TagsContext = createContext<TagsContextType | undefined>(undefined);

export const TagsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(false);
  const { organizationId } = useOrganization();

  // Carregar tags da organização
  const refreshTags = useCallback(async () => {
    if (!organizationId) {
      setTags([]);
      return;
    }

    try {
      setLoading(true);
      console.log('[TagsContext] Carregando tags globais da organização:', organizationId);

      const { data, error } = await supabase
        .from('organization_tags')
        .select('*')
        .eq('organization_id', organizationId)
        .order('name');

      if (error) {
        console.error('[TagsContext] Erro ao carregar tags:', error);
        return;
      }

      const loadedTags = data?.map(tag => ({
        id: tag.id,
        name: tag.name,
        color: tag.color
      })) || [];

      console.log('[TagsContext] Tags carregadas:', loadedTags.length);
      setTags(loadedTags);
    } catch (err) {
      console.error('[TagsContext] Exceção ao carregar tags:', err);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  // Carregar tags quando a organização mudar
  useEffect(() => {
    refreshTags();
  }, [refreshTags]);

  // Adicionar nova tag
  const addTag = useCallback(async (name: string, color: string, createdBy: string): Promise<Tag | null> => {
    if (!organizationId) return null;

    try {
      console.log('[TagsContext] Adicionando nova tag:', { name, color });

      const { data, error } = await supabase
        .from('organization_tags')
        .insert({
          organization_id: organizationId,
          name: name.trim(),
          color: color,
          created_by: createdBy
        })
        .select()
        .single();

      if (error) {
        console.error('[TagsContext] Erro ao adicionar tag:', error);
        return null;
      }

      const newTag: Tag = {
        id: data.id,
        name: data.name,
        color: data.color
      };

      // Atualizar estado local
      setTags(prev => [...prev, newTag].sort((a, b) => a.name.localeCompare(b.name)));
      console.log('[TagsContext] Tag adicionada com sucesso');

      return newTag;
    } catch (err) {
      console.error('[TagsContext] Exceção ao adicionar tag:', err);
      return null;
    }
  }, [organizationId]);

  // Atualizar tag existente
  const updateTag = useCallback(async (id: string, name: string, color: string): Promise<boolean> => {
    if (!organizationId) return false;

    try {
      console.log('[TagsContext] Atualizando tag:', { id, name, color });

      const { error: updateError } = await supabase
        .from('organization_tags')
        .update({
          name: name.trim(),
          color: color
        })
        .eq('id', id);

      if (updateError) {
        console.error('[TagsContext] Erro ao atualizar tag:', updateError);
        return false;
      }

      // Atualizar estado local
      setTags(prev => prev.map(tag =>
        tag.id === id
          ? { id, name: name.trim(), color }
          : tag
      ).sort((a, b) => a.name.localeCompare(b.name)));

      console.log('[TagsContext] Tag atualizada com sucesso');

      // Atualizar todas as tarefas que usam esta tag (em background)
      const { data: orgProjects } = await supabase
        .from('projects')
        .select('id')
        .eq('organization_id', organizationId);

      const projectIds = orgProjects?.map(p => p.id) || [];

      if (projectIds.length > 0) {
        const { data: tasks } = await supabase
          .from('project_kanban_tasks')
          .select('id, labels, project_id')
          .in('project_id', projectIds)
          .not('labels', 'is', null);

        // Encontrar e atualizar tarefas que usam esta tag
        const oldTag = tags.find(t => t.id === id);
        if (oldTag && tasks) {
          for (const task of tasks) {
            if (task.labels && Array.isArray(task.labels)) {
              const hasTag = task.labels.some((label: any) =>
                label.id === id || label.name === oldTag.name
              );

              if (hasTag) {
                const updatedLabels = task.labels.map((label: any) =>
                  (label.id === id || label.name === oldTag.name)
                    ? { id, name: name.trim(), color }
                    : label
                );

                await supabase
                  .from('project_kanban_tasks')
                  .update({ labels: updatedLabels })
                  .eq('id', task.id);
              }
            }
          }
        }
      }

      return true;
    } catch (err) {
      console.error('[TagsContext] Exceção ao atualizar tag:', err);
      return false;
    }
  }, [organizationId, tags]);

  // Deletar tag
  const deleteTag = useCallback(async (id: string): Promise<boolean> => {
    if (!organizationId) return false;

    try {
      console.log('[TagsContext] Verificando uso da tag antes de excluir:', id);

      // Buscar projetos da organização
      const { data: orgProjects, error: projectError } = await supabase
        .from('projects')
        .select('id')
        .eq('organization_id', organizationId);

      if (projectError) {
        console.error('[TagsContext] Erro ao buscar projetos:', projectError);
        return false;
      }

      const projectIds = orgProjects?.map(p => p.id) || [];

      // Verificar se a tag está em uso
      const { data: tasks, error: checkError } = await supabase
        .from('project_kanban_tasks')
        .select('id, labels, project_id')
        .in('project_id', projectIds)
        .not('labels', 'is', null);

      if (checkError) {
        console.error('[TagsContext] Erro ao verificar uso da tag:', checkError);
        return false;
      }

      const tasksUsingTag = tasks?.filter(task => {
        return task.labels && Array.isArray(task.labels) &&
               task.labels.some((label: any) => label.id === id);
      });

      if (tasksUsingTag && tasksUsingTag.length > 0) {
        console.warn(`[TagsContext] Tag está sendo usada em ${tasksUsingTag.length} tarefa(s)`);
        throw new Error(`Não é possível excluir esta etiqueta pois está sendo usada em ${tasksUsingTag.length} tarefa(s).`);
      }

      // Deletar da tabela global
      const { error: deleteError } = await supabase
        .from('organization_tags')
        .delete()
        .eq('id', id);

      if (deleteError) {
        console.error('[TagsContext] Erro ao deletar tag:', deleteError);
        return false;
      }

      // Atualizar estado local
      setTags(prev => prev.filter(tag => tag.id !== id));
      console.log('[TagsContext] Tag deletada com sucesso');

      return true;
    } catch (err) {
      console.error('[TagsContext] Exceção ao deletar tag:', err);
      throw err;
    }
  }, [organizationId]);

  const value = useMemo(
    () => ({ tags, loading, refreshTags, addTag, updateTag, deleteTag }),
    [tags, loading, refreshTags, addTag, updateTag, deleteTag]
  );

  return (
    <TagsContext.Provider value={value}>
      {children}
    </TagsContext.Provider>
  );
};

export const useTags = (): TagsContextType => {
  const context = useContext(TagsContext);
  if (context === undefined) {
    throw new Error('useTags must be used within a TagsProvider');
  }
  return context;
};
